import { inject, Injectable } from '@angular/core';
import { CalAngularService } from '@cvx/cal-angular';
import { AppConfig } from 'src/app/app-config.service';
import { Observable, switchMap } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class DevPortalHttpClient {
    private readonly authService = inject(CalAngularService);
    private readonly httpClient = inject(HttpClient);
    private readonly baseUrl = AppConfig.settings.apiUrl;

    public Get<T>(url: string, query?: any): Observable<T> {
        return this.getUserAuthToken().pipe(
            switchMap((token) => {
                const headers = this.createHeaders(token);
                const params = this.createParams(query);
                return this.httpClient.get<T>(this.baseUrl + url, {
                    headers,
                    params,
                });
            })
        );
    }

    public Post<T, TResult>(
        url: string,
        payload: T,
        query?: any
    ): Observable<TResult> {
        return this.getUserAuthToken().pipe(
            switchMap((token) => {
                const headers = this.createHeaders(token);
                const params = this.createParams(query);
                return this.httpClient.post<TResult>(
                    this.baseUrl + url,
                    payload,
                    { headers, params }
                );
            })
        );
    }

    public Patch<T, TResult>(
        url: string,
        payload: T,
        query?: any
    ): Observable<TResult> {
        return this.getUserAuthToken().pipe(
            switchMap((token) => {
                const headers = this.createHeaders(token);
                const params = this.createParams(query);
                return this.httpClient.patch<TResult>(
                    this.baseUrl + url,
                    payload,
                    { headers, params }
                );
            })
        );
    }

    public Put<T, TResult>(
        url: string,
        payload: T,
        query?: any
    ): Observable<TResult> {
        return this.getUserAuthToken().pipe(
            switchMap((token) => {
                const headers = this.createHeaders(token);
                const params = this.createParams(query);
                return this.httpClient.put<TResult>(
                    this.baseUrl + url,
                    payload,
                    { headers, params }
                );
            })
        );
    }

    public Delete<T>(url: string, query?: any): Observable<T> {
        return this.getUserAuthToken().pipe(
            switchMap((token) => {
                const headers = this.createHeaders(token);
                const params = this.createParams(query);
                return this.httpClient.delete<T>(this.baseUrl + url, {
                    headers,
                    params,
                });
            })
        );
    }

    private createHeaders(token: string): HttpHeaders {
        return new HttpHeaders().set('Authorization', `Bearer ${token}`);
    }

    private createParams(query?: any): HttpParams {
        return new HttpParams({ fromObject: { ...query } });
    }

    public getUserAuthToken(scope?: string[]): Observable<string> {
        const scopes = scope ?? [`${AppConfig.settings.clientId}/user_impersonation`];
        return this.authService.getAADToken(
            scopes,
            false
        ) as Observable<string>;
    }
}
